/**
 * Save the DIFFERENCES, not the defaults.
 *
 * Every element the editor creates carries the full style sheet its type knows
 * about — roughly 60 keys for a text box, most of them never touched. Stored as
 * they are, a two-frame card with nine elements is thousands of lines of
 * `"noiseType": "mono"`, and every save ships all of it to R2.
 *
 * So a document is compacted on its way out: anything that still equals what the
 * factory in documentSchema would have produced for that element is dropped, and
 * `normalizeDocument` puts the defaults back on the way in. The round trip is
 * exact — the editor works with the same fully-populated objects it always did,
 * and old fully-expanded documents keep loading unchanged.
 *
 * The rule is deliberately "equal to the factory default", not a hand-written
 * list of keys: the defaults have exactly one home (the factories), so adding a
 * style later cannot leave this file quietly wrong.
 */
import { createFrame, defaultElementFor } from './documentSchema';

/**
 * Keys that are kept even when they match the default.
 *
 * Two reasons to protect one: it identifies the element (dropping it would make
 * the document unreadable), or it is CONTENT rather than styling. A text box
 * that happens to still say "Double-click to edit" must keep saying that even if
 * the factory's placeholder text is reworded next year.
 */
const KEEP_ALWAYS = new Set([
    'id', 'type', 'x', 'y', 'width', 'height', 'zIndex', 'frameId',
    'content', 'src', 'shape', 'placeholderKey',
]);

/** Values that mean "nothing here" — dropped whatever the factory says. */
const isEmptyish = (value) => value === null
    || value === undefined
    || (Array.isArray(value) && value.length === 0)
    || (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

/** Structural equality, enough for the scalars, arrays and flat maps stored here. */
function same(a, b) {
    if (a === b) return true;
    if (typeof a !== typeof b || a === null || b === null) return false;
    if (typeof a !== 'object') return false;
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => same(a[k], b[k]));
}

let frameRef = null;
function defaultFrame() {
    if (!frameRef) frameRef = createFrame();
    return frameRef;
}

/** One element, minus everything that is still factory-standard. */
export function compactElement(el) {
    if (!el || typeof el !== 'object') return el;
    const ref = defaultElementFor(el.type, el.shape);
    if (!ref) return el;
    const out = {};

    Object.keys(el).forEach((key) => {
        const value = el[key];
        if (value === undefined) return;
        if (KEEP_ALWAYS.has(key)) { out[key] = value; return; }
        if (key === 'styles') return; // handled below
        // `null` / `[]` / `{}` are how this schema spells "not set" — a stored
        // `"crop": null` or `"paletteBase": null` says nothing a missing key
        // doesn't, and every reader already treats absent as unset.
        if (isEmptyish(value)) return;
        if (key in ref && same(value, ref[key])) return;
        out[key] = value;
    });

    const styles = el.styles || {};
    const refStyles = ref.styles || {};
    const nextStyles = {};
    Object.keys(styles).forEach((key) => {
        const value = styles[key];
        if (value === undefined) return;
        if (key in refStyles && same(value, refStyles[key])) return;
        nextStyles[key] = value;
    });
    if (Object.keys(nextStyles).length) out.styles = nextStyles;

    return out;
}

/** One frame, minus the defaults `normalizeFrame` will put back. */
export function compactFrame(frame) {
    if (!frame || typeof frame !== 'object') return frame;
    const ref = defaultFrame();
    const out = {};
    Object.keys(frame).forEach((key) => {
        const value = frame[key];
        if (value === undefined) return;
        // Identity and geometry always travel; `name` is the user's label and
        // `clipContent` is load-bearing in the false direction — a frame saved
        // before the flag existed must stay unclipped, so only `true` is worth
        // storing and `false` is the absent case.
        if (['id', 'name', 'x', 'y', 'width', 'height', 'presetId'].includes(key)) {
            out[key] = value;
            return;
        }
        if (key === 'clipContent') {
            if (value) out.clipContent = true;
            return;
        }
        if (isEmptyish(value)) return;
        if (key in ref && same(value, ref[key])) return;
        out[key] = value;
    });
    return out;
}

/**
 * A whole document ready for storage.
 *
 * `canvas` — the legacy single-artboard mirror of frame 1 — is kept as it is:
 * it is seven fields, and it is what a template saved before frames existed is
 * read back through.
 */
export function compactDocument(doc) {
    if (!doc || typeof doc !== 'object') return doc;
    const out = { ...doc };
    if (Array.isArray(doc.elements)) out.elements = doc.elements.map(compactElement);
    if (Array.isArray(doc.frames)) out.frames = doc.frames.map(compactFrame);
    if (Array.isArray(doc.groups) && !doc.groups.length) delete out.groups;
    return out;
}

/**
 * How much a compaction saved — used by the editor's own logging, and handy
 * when checking a template by hand.
 */
export function compactionStats(doc) {
    const before = JSON.stringify(doc || {}).length;
    const after = JSON.stringify(compactDocument(doc) || {}).length;
    return { before, after, saved: before - after, ratio: before ? after / before : 1 };
}
